import type { HeroProps } from '@/components/home/Hero';
import type { SwitchingEuropeProps } from '@/components/home/SwitchingEurope';
import type {
  CustomersReviewsProps,
  GenericProductsProps,
  HowItWorksProps,
  InstantEverythingProps,
  ProductsProps,
  TrustProps,
} from '@/components/shared';
import type { BasePageConfig } from '@/pageConfig/BasePageConfig';
import { sharedConfig } from '@/pageConfig/shared';
import aRated from '@/static/assets/home/a-rated.png';
import bCorp from '@/static/assets/home/b-corp.png';
import roomLogo from '@/static/assets/home/fr-products-logo-2024.svg';
import room from '@/static/assets/home/living-room-with-checkmarks.svg';
import ogImage from '@/static/assets/home/og-fr-en.png';
import pizzaDesktop from '@/static/assets/home/pizza_desktop_en-FR.svg';
import pizzaMobile1 from '@/static/assets/home/pizza_mobile_1.svg';
import pizzaMobile2 from '@/static/assets/home/pizza_mobile_2.svg';
import pizzaMobile3 from '@/static/assets/home/pizza_mobile_3.svg';
import chatFrDesktopLottieConfig from '@/static/genericProducts/chatEnDesktopLottie.json';
import chatFrLottieConfig from '@/static/genericProducts/chatEnLottie.json';
import mapDesktopLottieConfig from '@/static/genericProducts/mapDesktopLottie.json';
import mapLottieConfig from '@/static/genericProducts/mapLottie.json';
import successDesktopLottieConfig from '@/static/genericProducts/successDesktopLottie.json';
import successLottieConfig from '@/static/genericProducts/successLottie.json';

const pizza = {
  desktop: pizzaDesktop,
  mobile: {
    1: pizzaMobile1,
    2: pizzaMobile2,
    3: pizzaMobile3,
  },
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  customersReviews: CustomersReviewsProps;
  genericProducts: GenericProductsProps;
  instantEverything: InstantEverythingProps;
  howItWorks: HowItWorksProps;
  trust: TrustProps;
  switchingEurope: SwitchingEuropeProps;
  products: ProductsProps;
}

// ORDER HERE MATTERS
export const config: Config = {
  seo: {
    ...sharedConfig['en-FR'].seo,
    ogImage: ogImage.src,
    description:
      'Discover Lemonade’s home insurance in France, offering fast, paperless sign-up, instant claims processing, and a commitment to transparency and social good.',
  },
  layout: {
    ...sharedConfig['en-FR'].layout,
    showAskUsAnything: true,
    alignLogoOnMobile: 'left',
    headerActions: {
      ...sharedConfig['en-FR'].layout.headerActions,
      CTAs: {
        ...sharedConfig['en-FR'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['en-FR'].layout.headerActions.CTAs.checkOurPrices,
          short: {
            singular: 'Check our price',
            plural: 'Check our prices',
          },
        },
      },
    },
  },
  hero: {
    heroType: 'frHero',
    title: 'Forget everything you\nknow about insurance',
    subtitle: 'Instant everything. Great prices. Big heart.',
    ctaLabel: 'Check our Prices',
    secondaryCta: {
      label: 'Already insured? We’ll help you switch',
      scrollToSection: 'switching',
    },
    cart: {
      singular: 'Back to quote',
      plural: 'Back to quotes',
    },
    assignRefToMainCta: false,
  },
  products: {
    title: 'Incredible Prices. No Paperwork. Cancel Any Time.',
    showDiamond: false,
    layout: {
      mt: [-10, -50],
    },
    products: [
      {
        title: 'Renters',
        subtitle: 'Coverage for your stuff inside and out of your home',
        price: 'FROM 4 €/MO',
        product: 'home_eu_renters',
        ctaLabelDesktop: 'Check our prices',
        ctaLabelMobile: 'Check prices',
        customUrl: '/onboarding/start?f=1&ownership=renter',
        alt: 'Renters Insurance',
      },
      {
        title: 'Homeowners',
        subtitle: 'Protection for your stuff, plus your actual home',
        price: 'FROM 10 €/MO',
        product: 'home_eu_owners',
        ctaLabelDesktop: 'Check our prices',
        ctaLabelMobile: 'Check prices',
        customUrl: '/onboarding/start?f=1&ownership=owner',
        alt: 'Homeowners Insurance',
      },
      {
        title: 'Landlords',
        subtitle: 'Coverage for your home while you’re renting it to a tenant',
        price: 'FROM 6 €/MO',
        product: 'landlords_eu',
        ctaLabelDesktop: 'Check our prices',
        ctaLabelMobile: 'Check prices',
        customUrl: '/onboarding/start?f=1&ownership=owner',
        alt: 'Landlords Insurance',
      },
    ],
    trackingLabel: 'home_fr',
    assignRefToMainCta: true,
    darkBackground: true,
  },
  customersReviews: {
    layout: {
      mt: [-30, 0],
    },
    title: 'Over 2,000,000 happy customers!',
    subtitle: `Lemonade has earned 4.9 stars in the App store, and is top-rated on <a href="https://fr.trustpilot.com/review/lemonade.com" target="_blank">Trustpilot</a>`,
    thirdTitle:
      'Lemonade is a publicly traded company (<b>NYSE : LMND</b>) and is backed\nby world reinsurance leaders',
    darkBackground: true,
    tweetsBackBandColor: '#f7f7f7',
  },
  instantEverything: {
    title: 'Instant Everything',
    subtitle:
      'Maya, our charming artificial intelligence bot, will craft the perfect coverage for you.\nIt couldn’t be easier, or faster',
    appSketchContentLeft: 'Get insured\n<b>In Secondes</b>',
    appSketchContentRight: 'Get paid\n<b>In Minutes</b>',
    darkBackground: true,
    adjustments: {
      contentSectionLeft: {
        left: { lg: '-425px' },
      },
      contentSectionRight: {
        right: { sm: '-158px', lg: '-410px' },
      },
    },
  },
  genericProducts: {
    title: 'Home insurance from 4 € for Renters<br/>and 10 € for Owners',
    subtitle: 'Sign up in seconds. Pay monthly. 100% digital',
    bgImage: room.src,
    productLogo: {
      alt: 'Label Excellence 2023',
      img: roomLogo.src,
      width: 95,
      height: 95,
    },
    layout: {
      mt: [-10, -30],
    },
  },
  howItWorks: {
    layout: {
      mt: [-10, -50],
    },
    title: 'How Lemonade Works',
    subtitle: `Lemonade reverses the traditional insurance model. We treat the premiums you pay as if it's your money, not ours. With Lemonade, everything becomes simple and transparent. We take a flat fee, pay claims super fast,\nand <a aria-labelledby="how-it-works-subtitle" href="/fr/en/giveback">give back</a> what’s left to causes you care about.*`,
    disclaimer:
      '* Subject to board discretion and the company meeting certain financial standards. <a href="/fr/en/faq#giveback">Read more here</a>',
    pizzaDesktop: pizza.desktop.src,
    pizzaMobile: [
      {
        image: pizza.mobile[1].src,
        text: 'A <b>flat fee</b> to </br> run everything',
      },
      {
        image: pizza.mobile[2].src,
        text: 'We pay claims <br/> <b>super fast</b>',
      },
      {
        image: pizza.mobile[3].src,
        text: `If there's money leftover,<br/> we <b>give it back to causes</b>`,
      },
    ],
    showBrandsImage: false,
    darkBackground: true,
  },
  trust: {
    bulletsLayout: 'horizontal',
    showTopBorder: true,
    darkBackground: true,
    bullets: [
      {
        name: 'social',
        title: 'Designed for Social Impact',
        text: 'Lemonade Inc. is a Public Benefit Corporation and certified B-Corp. Social impact is part of our legal mission and business model - not just marketing fluff',
        image: {
          src: bCorp,
          width: 103,
          height: 88,
        },
      },
      {
        name: 'rating',
        title: 'A-Rated and Backed by Giants',
        text: 'Lemonade is rated A-Exceptional, fully licensed, and reinsured by some of the most trusted names on the planet',
        image: {
          src: aRated,
          width: 100,
          height: 89,
        },
      },
    ],
  },
  switchingEurope: {
    title: 'Already insured?\nSwitch to Lemonade instantly',
    ctaLabel: 'Check prices and switch',
    leftLottieConfig: chatFrLottieConfig,
    centerLottieConfig: mapLottieConfig,
    rightLottieConfig: successLottieConfig,
    // desktop
    leftDesktopLottieConfig: chatFrDesktopLottieConfig,
    centerDesktopLottieConfig: mapDesktopLottieConfig,
    rightDesktopLottieConfig: successDesktopLottieConfig,
    steps: ['Get your\nLemonade policy', 'Give us your old\npolicy details', 'We’ll handle all\nthe paperwork'],
  },
};
